// src/pages/MeetingRoom/hooks/useMeetingTimer.js
import { useEffect, useRef, useState } from "react";
import { formatElapsed } from "../../../utils/formatTime";
import { useToast } from "../../../components/Toaster/use-toast";

/**
 * Tracks elapsed time during a meeting session.
 * Handles start, stop, reset, and time-based reminders (e.g., 1-minute warning).
 */
export default function useMeetingTimer({ sessionActive, meetingData, trainingSession, sendEvent }) {
    const { toast } = useToast();
  const [elapsed, setElapsed] = useState(0);          // elapsed seconds
  const elapsedRef = useRef(0);
  const timerRef = useRef(null);
  const reminderTriggered = useRef(false);

  // ─────────────────────────────
  // 🔹 Start / Stop Timer
  // ─────────────────────────────
  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const resetTimer = () => {
    stopTimer();
    elapsedRef.current = 0;
    setElapsed(0);
    reminderTriggered.current = false;
  };

  // ─────────────────────────────
  // 🔹 Auto Start / Stop based on sessionActive
  // ─────────────────────────────
  useEffect(() => {
    if (sessionActive) {
      startTimer();
    } else {
      stopTimer();
    }
    return () => stopTimer();
  }, [sessionActive]);

  // ─────────────────────────────
  // 🔹 1-Minute Remaining Reminder
  // ─────────────────────────────
  useEffect(() => {
    const duration = trainingSession?.duration || meetingData?.duration || 30;
    const maxSeconds = duration * 60;
    const oneMinuteLeft = maxSeconds - 60;

    if (
      sessionActive &&
      !reminderTriggered.current &&
      elapsed >= oneMinuteLeft
    ) {
      reminderTriggered.current = true;

      // Trigger in-session AI reminder
      if (sendEvent) {
        sendEvent({
          type: "session.update",
          session: {
            instructions: `Reminder: You have 1 minute remaining. Wrap up naturally — do not end early unless the user explicitly indicates they are done.`,
          },
        });
      }

      // User-facing toast
      toast({
        title: "1 minute remaining",
        description: "The meeting is almost over — wrap up your thoughts.",
        duration: 5000,
      });
    }
  }, [elapsed, sessionActive, meetingData, trainingSession, sendEvent, toast]);

  // ─────────────────────────────
  // 🔹 Cleanup on unmount
  // ─────────────────────────────
  useEffect(() => {
    return () => stopTimer();
  }, []);

  // ─────────────────────────────
  // 🔹 Return
  // ─────────────────────────────
  return {
    elapsed,
    elapsedRef,
    startTimer,
    stopTimer,
    resetTimer,
    formattedElapsed: formatElapsed(elapsed),
  };
}
